import { Add, Delete, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FlowTestMeasurementForm as FlowTestMeasurementFormType } from "../../../types/forms/flowTest";
import {
  FlowTestMeasurementGroupResponse
} from "../../../types/model/flowTestMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import { formatTimestamp } from "../../../utils";
import { MeasurementInGroup } from "./FlowTestMeasurements";
import { AddMeasurementRow, EditCell } from "./MeasurementCells";

interface GroupRowProps {
  group: FlowTestMeasurementGroupResponse;
  variables: VariableResponse[];
  entityId: number;
  open: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onUpdate: (id: number, values: FlowTestMeasurementFormType) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onCreate: (values: FlowTestMeasurementFormType) => Promise<void>;
  flowTestId: number;
  isEditing: boolean;
  isSaving: boolean;
}

export const GroupRow = ({
  group,
  variables,
  entityId,
  open,
  canEdit,
  onToggle,
  onUpdate,
  onDelete,
  onCreate,
  flowTestId,
  isEditing,
  isSaving,
}: GroupRowProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [addingVariable, setAddingVariable] = useState(false);
  const [editingMeasurementId, setEditingMeasurementId] = useState<number | null>(null);

  const [editBuffer, setEditBuffer] = useState<
    Record<number, { value: string; flag: string; notes: string }>
  >({});

  const measurements: MeasurementInGroup[] = group.measurements.map((m) => ({
    ...m,
    measuredCaptureId: group.measuredCaptureId,
  }));

  const startEdit = (m: MeasurementInGroup) => {
    setEditingMeasurementId(m.id);

    setEditBuffer((prev) => ({
      ...prev,
      [m.id]: {
        value: String(m.value ?? ""),
        flag: m.flag ?? "",
        notes: m.notes ?? "",
      },
    }));
  };

  const cancelEdit = () => {
    setEditingMeasurementId(null);
  };

  const saveEdit = async (m: MeasurementInGroup) => {
    const buffer = editBuffer[m.id];

    await onUpdate(m.id, {
      value: Number(buffer.value),
      variableId: m.variableId,
      measuredCaptureId: m.measuredCaptureId,
      measuredAt: group.measuredAt,
      testingTime: group.testingTime,
      flag: buffer.flag || undefined,
      notes: buffer.notes || undefined,
    });

    setEditingMeasurementId(null);
  };

  let timeDisplay = "—";

  if (group.testingTime != null) {
    timeDisplay = `${group.testingTime} ${t("generic.units.minute")}`;
  } else if (group.measuredAt) {
    timeDisplay = formatTimestamp(group.measuredAt);
  }

  const variableSummary = measurements.map((m) => m.variableName).join(", ");

  return (
    <React.Fragment>
      <TableRow
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          "& > *": { borderBottom: open ? "unset" : undefined },
          backgroundColor: open ? theme.palette.action.selected : undefined,
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <TableCell sx={{ width: 40, py: 1 }}>
          {open ? ( <KeyboardArrowUp fontSize="small" /> ) : ( <KeyboardArrowDown fontSize="small" /> )}
        </TableCell>
        <TableCell>{timeDisplay}</TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          {group.measuredCaptureName}
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {t("flow-tests.flow-test-measurement.measurements-count", {
              count: measurements.length,
            })}{" "}
            {!open && (
              <Typography component="span" variant="caption" color="text.disabled">
                - {variableSummary}
              </Typography>
            )}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow sx={{ backgroundColor: open ? theme.palette.action.hover : undefined }}>
        <TableCell colSpan={4} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2 }}>
              <Table size="small" sx={{ mb: 1, tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      backgroundColor: "background.paper"
                    }}
                  >
                    <TableCell 
                      className="variable-col" 
                      sx={{ 
                        fontWeight: 600, 
                        verticalAlign: "top",
                        width: canEdit ? undefined : "40%",
                      }}
                    >
                      {t("flow-tests.flow-test-measurement.variable")}
                    </TableCell>

                    <TableCell className="value-col" sx={{ fontWeight: 600, verticalAlign: "top", width: "50%" }}>
                      {t("flow-tests.flow-test-measurement.value")}
                    </TableCell>

                    <TableCell
                      className="actions-col"
                      sx={{ textAlign: "end", pr: 3, verticalAlign: "top" }}
                    >
                      {canEdit ? t("generic.actions") : null}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {measurements.map((m) => {
                    const isEditing = editingMeasurementId === m.id;

                    const buffer = editBuffer[m.id] || {
                      value: "",
                      flag: "",
                      notes: "",
                    };

                    return (
                      <TableRow key={m.id}>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Typography variant="body2">
                            {m.variableName}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ verticalAlign: "top" }}>
                          <EditCell
                            measurement={m}
                            isEditing={isEditing}
                            value={buffer.value}
                            flag={buffer.flag}
                            notes={buffer.notes}
                            setValue={(v) =>
                              setEditBuffer((p) => ({
                                ...p,
                                [m.id]: { ...p[m.id], value: v },
                              }))
                            }
                            setFlag={(v) =>
                              setEditBuffer((p) => ({
                                ...p,
                                [m.id]: { ...p[m.id], flag: v },
                              }))
                            }
                            setNotes={(v) =>
                              setEditBuffer((p) => ({
                                ...p,
                                [m.id]: { ...p[m.id], notes: v },
                              }))
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "flex-end",
                              width: "100%",
                              gap: 1,
                            }}
                          >
                            {isEditing && canEdit ? (
                              <>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEdit();
                                  }}
                                >
                                  <EditOffIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveEdit(m);
                                  }}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              canEdit && (
                                <>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEdit(m);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDelete(m.id);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              )
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* ADD ROW */}
              {addingVariable ? (
                <AddMeasurementRow
                  flowTestId={flowTestId}
                  entityId={entityId}
                  measuredCaptureId={group.measuredCaptureId}
                  measuredAt={group.measuredAt}
                  testingTime={group.testingTime}
                  onCreate={onCreate}
                  onCancel={() => setAddingVariable(false)}
                  variables={variables}
                  isEditing={isEditing}
                  isSaving={isSaving}
                />
              ) : (
                (canEdit && (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddingVariable(true);
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    {t("flow-tests.flow-test-measurement.add-variable")}
                  </Button>
                ))
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};